import clsx from 'clsx'
import type { KeyboardEventHandler, PropsWithChildren, Ref } from 'react'
import React, { useEffect } from 'react'
import { useAutocomplete } from '../Autocomplete/Autocomplete'
import { useDisposables } from '../hooks/useDisposables'
import { useSyncRefs } from '../hooks/useSyncRefs'
import Portal from '../Portal/Portal'
import { Focus } from '../utilities/calculateActiveIndex'
import { Keys } from '../utilities/keyboard'
import { ActionTypes } from './Actions'
import { useSubmenu } from './Item'
import { useMenu } from './Menu'

function Items(
  { children }: PropsWithChildren<Record<string, unknown>>,
  ref: Ref<HTMLUListElement>,
): JSX.Element | null {
  const d = useDisposables()
  const { id: buttonId } = useSubmenu()?.[0] || {}
  const [state, dispatch] = useMenu()
  const itemsRef = useSyncRefs(state.itemsRef, ref)
  const parent = state.parentRef?.current?.parentNode?.parentNode

  const onKeyDown: KeyboardEventHandler<HTMLUListElement> = (event) => {
    switch (event.key) {
      case Keys.Space:
        if (state.search !== '') {
          event.preventDefault()
          event.stopPropagation()

          return dispatch({ type: ActionTypes.Search, value: event.key })
        }

      // This case purposefully falls through to allow the space key to close the menu when not searching
      case Keys.Enter:
        event.preventDefault()
        event.stopPropagation()

        if (event.repeat) {
          return
        }

        if (state.activeItemIndex !== null) {
          const { id } = state.items[state.activeItemIndex]

          document.getElementById(id)?.click()

          return
        }

        dispatch({ type: ActionTypes.CloseMenu })

        d.nextFrame(() => {
          if (state.headless) {
            state.buttonRef.current?.parentElement?.parentElement?.focus({
              preventScroll: true,
            })
          } else {
            if (state.focusRef?.current) {
              state.focusRef.current?.focus({ preventScroll: true })
            } else {
              state.buttonRef.current?.focus({ preventScroll: true })
            }
          }
        })

        break
      case Keys.ArrowDown:
        event.preventDefault()
        event.stopPropagation()

        return dispatch({ type: ActionTypes.GoToItem, focus: Focus.Next })
      case Keys.ArrowUp:
        event.preventDefault()
        event.stopPropagation()

        return dispatch({ type: ActionTypes.GoToItem, focus: Focus.Previous })
      case Keys.ArrowRight:
        if (state.activeItemIndex !== null) {
          const { id, dataRef } = state.items[state.activeItemIndex]

          if (dataRef.current.hasSubmenu && !state.openSubmenuItemId) {
            event.preventDefault()
            event.stopPropagation()

            if (event.repeat) {
              return
            }

            document.getElementById(id)?.click()
          }
        }

        break
      case Keys.ArrowLeft:
        if (state.parentRef) {
          event.preventDefault()
          event.stopPropagation()

          dispatch({ type: ActionTypes.CloseMenu })
          d.nextFrame(() => {
            state.buttonRef.current?.focus({ preventScroll: true })
          })
        }
        break
      case Keys.Escape:
        event.preventDefault()
        event.stopPropagation()

        dispatch({ type: ActionTypes.CloseMenu })
        d.nextFrame(() => {
          if (state.headless) {
            state.buttonRef.current?.parentElement?.parentElement?.focus({
              preventScroll: true,
            })
          } else {
            if (state.focusRef?.current) {
              state.focusRef.current?.focus({ preventScroll: true })
            } else {
              state.buttonRef.current?.focus({ preventScroll: true })
            }
          }
        })

        break
      case Keys.Tab:
        event.stopPropagation()

        return event.preventDefault()
      default:
        if (state.searchable && event.key.length === 1) {
          event.stopPropagation()

          dispatch({ type: ActionTypes.Search, value: event.key })
          d.setTimeout(() => dispatch({ type: ActionTypes.ClearSearch }), 350)
        }

        break
    }
  }

  // When menu items change, selected item can have a different index in the list
  useEffect(() => {
    if (state.listbox) {
      dispatch({ type: ActionTypes.RefreshSelectedIndex })
    }
  }, [children, dispatch, state.listbox])

  const { multi, minWidth } = useAutocomplete()?.[0] || {}

  const items = (
    // this rule is disabled by design, as listbox is an interactive element
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <ul
      {...(multi || state.integratedMultiSelect
        ? { 'aria-multiselectable': true }
        : {})}
      aria-activedescendant={
        state.activeItemIndex === null
          ? undefined
          : state.items[state.activeItemIndex]?.id
      }
      aria-labelledby={buttonId || `coconut-menu-button-${state.id}`}
      className={clsx(
        'absolute z-9998 flex flex-col py-4 overflow-hidden overflow-y-auto rounded bg-white-100 shadow-5 focus:outline-none',
        state.listbox ? 'w-auto' : '',
        state.show ? null : 'hidden',
      )}
      id={`coconut-menu-${state.id}`}
      onKeyDown={onKeyDown}
      ref={itemsRef}
      role={state.listbox ? 'listbox' : 'menu'}
      style={{ minWidth }}
      tabIndex={0}
    >
      {children}
    </ul>
  )

  return state.parentRef ? (
    <Portal isMenu open={state.show} parent={parent}>
      {items}
    </Portal>
  ) : (
    <Portal open={state.show}>
      <div>{items}</div>
    </Portal>
  )
}

export default Items
